import { useEffect, useRef, useState } from "react";
import { Link, useParams, useSearchParams } from "react-router-dom";
import api, { formatApiErrorDetail } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { SiteNav as PublicNav } from "@/components/media/SiteNav";
import { Eyebrow } from "@/components/ladder/Offers";
import { CheckCircle2, XCircle } from "lucide-react";

const UNLOCKED = {
  popup_profits: ["Enter Smash Academy", "/academy-dashboard"],
};

/** Kicks off a Stripe Checkout session, then redirects. */
export function Buy() {
  const { lookupKey } = useParams();
  const [error, setError] = useState("");
  const fired = useRef(false);

  useEffect(() => {
    if (fired.current) return;
    fired.current = true;
    api
      .post("/payments/checkout", { lookup_key: lookupKey, origin_url: window.location.origin })
      .then(({ data }) => {
        window.location.href = data.checkout_url;
      })
      .catch((err) => setError(formatApiErrorDetail(err.response?.data?.detail) || err.message));
  }, [lookupKey]);

  return (
    <div data-testid="buy-page">
      <PublicNav />
      <div className="mx-auto max-w-lg px-5 py-24 text-center">
        {error ? (
          <>
            <XCircle className="mx-auto text-destructive" size={28} />
            <h1 className="neon mt-6 text-3xl">Checkout didn't open</h1>
            <p data-testid="buy-error" className="mt-4 text-sm text-destructive">{error}</p>
            <Link to="/start" className="mono mt-8 inline-block text-[11px] tracking-widest text-primary hover:underline">
              ← BACK TO PRICING
            </Link>
          </>
        ) : (
          <>
            <Eyebrow>SECURE CHECKOUT</Eyebrow>
            <h1 className="neon mt-4 text-3xl">Opening Stripe…</h1>
            <p className="mono mt-4 text-[11px] tracking-widest text-muted-foreground">
              DON'T CLOSE THIS TAB
            </p>
          </>
        )}
      </div>
    </div>
  );
}

export function PaymentSuccess() {
  const [params] = useSearchParams();
  const sessionId = params.get("session_id");
  const { refreshUser } = useAuth();
  const [status, setStatus] = useState("checking");
  const [lookupKey, setLookupKey] = useState("");

  useEffect(() => {
    if (!sessionId) {
      setStatus("error");
      return;
    }
    let tries = 0;
    let timer;
    const poll = async () => {
      tries += 1;
      try {
        const { data } = await api.get(`/payments/status/${sessionId}`);
        setLookupKey(data.lookup_key || "");
        if (data.payment_status === "paid") {
          await refreshUser().catch(() => {});
          setStatus("paid");
          return;
        }
        if (["failed", "expired"].includes(data.payment_status)) {
          setStatus("error");
          return;
        }
      } catch {
        setStatus("error");
        return;
      }
      if (tries >= 10) {
        setStatus("pending");
        return;
      }
      timer = setTimeout(poll, 2000);
    };
    poll();
    return () => clearTimeout(timer);
  }, [sessionId]); // eslint-disable-line react-hooks/exhaustive-deps

  const [label, to] = UNLOCKED[lookupKey] || ["Go to the Academy", "/academy-dashboard"];

  return (
    <div data-testid="payment-success-page">
      <PublicNav />
      <div className="mx-auto max-w-lg px-5 py-24 text-center">
        {status === "checking" && (
          <>
            <Eyebrow>CONFIRMING WITH STRIPE</Eyebrow>
            <h1 className="neon mt-4 text-3xl">Checking your payment…</h1>
          </>
        )}
        {status === "paid" && (
          <>
            <CheckCircle2 className="mx-auto text-primary" size={30} />
            <h1 className="neon mt-6 text-4xl">You're in.</h1>
            <p className="mt-4 text-sm text-muted-foreground">
              Payment confirmed. Your access is on your account for good.
            </p>
            <Link
              to={to}
              data-testid="payment-success-cta"
              className="display mt-8 inline-flex rounded-sm bg-primary px-7 py-4 text-xl uppercase text-primary-foreground transition-colors hover:bg-[#E6C800]"
            >
              {label}
            </Link>
          </>
        )}
        {status === "pending" && (
          <>
            <h1 className="neon mt-4 text-3xl">Still processing</h1>
            <p data-testid="payment-pending" className="mt-4 text-sm text-muted-foreground">
              Stripe hasn't confirmed yet. Refresh this page in a minute — nothing is lost.
            </p>
          </>
        )}
        {status === "error" && (
          <>
            <XCircle className="mx-auto text-destructive" size={28} />
            <h1 className="neon mt-6 text-3xl">We couldn't confirm that</h1>
            <p data-testid="payment-error" className="mt-4 text-sm text-muted-foreground">
              No access was granted. If you were charged, contact Smash HQ and we'll sort it.
            </p>
            <Link to="/start" className="mono mt-8 inline-block text-[11px] tracking-widest text-primary hover:underline">
              ← BACK TO PRICING
            </Link>
          </>
        )}
      </div>
    </div>
  );
}

export function PaymentCancel() {
  return (
    <div data-testid="payment-cancel-page">
      <PublicNav />
      <div className="mx-auto max-w-lg px-5 py-24 text-center">
        <h1 className="neon text-4xl">Checkout cancelled</h1>
        <p className="mt-4 text-sm text-muted-foreground">
          Nothing was charged. The free Starter Guide and the first two Academy milestones are still
          open to you.
        </p>
        <Link
          to="/start"
          data-testid="cancel-back-cta"
          className="display mt-8 inline-flex rounded-sm border border-primary px-7 py-4 text-xl uppercase text-primary transition-colors hover:bg-primary hover:text-primary-foreground"
        >
          Back to pricing
        </Link>
      </div>
    </div>
  );
}
